import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { EducationApi } from '../../apis/education/education.api';
import { ContactAuthHelper } from '@core/helpers/contact-auth.helper';
import { RehabilitationApi } from '../../apis/rehabilitation/rehabilitation.api';
import { ServiceApi } from '../../apis/service/service.api';
import { ISignIn } from '../../interfaces/interface';
import { ReusableProgramsMethodsService } from '../../services/reusable-programs-methods.service';
import { StorageService } from '../../services/storage.service';
import { SignInGoogleComponent } from './components/google/google.component';
import { RequiredAsteriskDirective } from '../../directive/required-asterisk.directive';
import { UserTypeEnum } from '@oranta-mis/sdk';
import { InputPhoneEmailComponent } from '../input-phone-email/input-phone-email.component';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    RouterModule,
    SignInGoogleComponent,
    RequiredAsteriskDirective,
    InputPhoneEmailComponent,
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent extends ReusableProgramsMethodsService implements OnInit, OnChanges {
  public myForm!: FormGroup;
  public passwordChecked: boolean = false;
  @Input({ required: true }) public isLoading: boolean = false;
  @Input({ required: true }) public whoSignIn!: UserTypeEnum;

  public readonly UserTypeEnum = UserTypeEnum;

  @Output() public signInData: EventEmitter<ISignIn> = new EventEmitter<ISignIn>();
  @Output() public googleCredentials: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    private readonly fb: FormBuilder,
    protected readonly storageService: StorageService,
    protected override educationApi: EducationApi,
    protected override rehabilitationApi: RehabilitationApi,
    protected override serviceApi: ServiceApi,
  ) {
    super(storageService, educationApi, rehabilitationApi, serviceApi);
    this.getPrograms();
  }

  private buildForm(type: UserTypeEnum): void {
    this.myForm =
      type === UserTypeEnum.patient
        ? this.fb.group({
            contact: [null, [Validators.required, ContactAuthHelper.emailOrPhoneValidator()]],
            password: [null, [Validators.required]],
          })
        : this.fb.group({
            email: [null, [Validators.required, Validators.email]],
            password: [null, [Validators.required]],
          });
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['whoSignIn']) {
      this.buildForm(this.whoSignIn);
    }
  }

  public onSubmit() {
    if (!this.myForm.valid) {
      this.myForm.markAllAsTouched();
      return;
    }

    const payload: ISignIn = { password: this.myForm.value.password };

    if (this.whoSignIn === UserTypeEnum.patient) {
      const resolvedContact = ContactAuthHelper.resolveContact(this.contact?.value);
      if (resolvedContact) {
        Object.assign(payload, resolvedContact);
      }
    } else {
      payload.email = (this.myForm.get('email')?.value ?? '').toString().trim();
    }

    this.signInData.emit(payload);
  }

  get contact(): FormControl {
    return this.myForm.get('contact') as FormControl;
  }
}
